import React from 'react';

import { CatalogGhostCard, catalogVisibilityBadge, CatalogVariantCard } from './CatalogVariantGrid';
import './CatalogCompareGrid.scss';

// Same order as the backend's CHANGE_TYPES, so the filter chips read by level: variant, siblings, then row fields.
export const CATALOG_CHANGE_LABELS = {
  renamed: 'Renamed',
  added: 'Variants added',
  removed: 'Variants removed',
  visibility: 'Visibility',
  siblings_moved: 'Siblings moved',
  siblings_added: 'Siblings added',
  siblings_removed: 'Siblings removed',
  siblings_excluded: 'Siblings excluded',
  non_size_tags_changed: 'Non-size tags changed',
  size_tags_changed: 'Size tags changed',
  skus_changed: 'SKUs changed',
  urls_changed: 'URLs changed',
  ids_changed: 'Shopify IDs changed'
};

export const CHANGE_TONES = Object.fromEntries(
  Object.keys(CATALOG_CHANGE_LABELS).map(type => [type, type === 'added' ? 'added' : type === 'removed' ? 'removed' : 'changed'])
);

const CARD_NOTE_LIMIT = 3;

const CARD_MIN_WIDTH = 180;
const CARD_GAP = 8;
const DIVIDER_WIDTH = 16;
const CARD_PADDING = 12;

export const catalogComparePairsPerRow = width => {
  const sideWidth = (width - DIVIDER_WIDTH - 4 * CARD_PADDING) / 2;
  return Math.max(1, Math.floor((sideWidth + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP)));
};

const CatalogCompareGrid = ({ currentLabel = 'Current', expandedKey, onToggle, pairs, pairsPerRow = 3, renderPanel, versionLabel = 'Draft' }) => {
  const rows = [];
  for (let index = 0; index < pairs.length; index += pairsPerRow) rows.push(pairs.slice(index, index + pairsPerRow));
  const sideStyle = { gridTemplateColumns: `repeat(${pairsPerRow}, minmax(0, 1fr))` };

  return (
    <div className='catalogs-compare-grid' style={{ '--catalog-compare-gap': `${DIVIDER_WIDTH}px`, '--catalog-compare-card-padding': `${CARD_PADDING}px` }}>
      <div className='catalogs-compare-content' style={{ minWidth: 2 * CARD_MIN_WIDTH + 4 * CARD_PADDING + DIVIDER_WIDTH }}>
        <div aria-hidden='true' className='catalogs-compare-row labels'>
          <div>{currentLabel}</div>
          <div />
          <div>{versionLabel}</div>
        </div>
        {rows.map(row => (
          <React.Fragment key={row[0].pairKey}>
            <div className='catalogs-compare-row'>
              <div className='catalogs-compare-side' style={sideStyle}>
                {row.map(pair => <PairCard isExpanded={expandedKey === pair.pairKey} key={pair.pairKey} onToggle={onToggle} pair={pair} side='current' />)}
              </div>
              <div aria-hidden='true' />
              <div className='catalogs-compare-side' style={sideStyle}>
                {row.map(pair => <PairCard isExpanded={expandedKey === pair.pairKey} key={pair.pairKey} onToggle={onToggle} pair={pair} side='proposed' />)}
              </div>
            </div>
            {row.some(pair => pair.pairKey === expandedKey) && <div className='catalogs-variant-panel-slot'>{renderPanel()}</div>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const PairCard = ({ isExpanded, onToggle, pair, side }) => {
  const variant = pair[side];
  if (!variant) {
    return (
      <CatalogGhostCard
        label={side === 'current' ? 'Not in the current catalog' : 'Not in the draft catalog'}
        note={pair.changeType === 'added' ? 'New variant in this run' : 'Variant retired'}
      />
    );
  }
  return (
    <CatalogVariantCard
      badges={pairBadges(pair, side)}
      isChanged={Boolean(pair.changeType)}
      isExpanded={isExpanded}
      note={cardNote(catalogChangeNotes(pair, side))}
      onToggle={() => onToggle(pair)}
      previousCounts={side === 'proposed' && pair.changeType && pair.current ? pair.current : null}
      variant={variant}
    />
  );
};

const pairBadges = (pair, side) => {
  const visibilityBadge =
    side === 'proposed' && pair.visibilityChange
      ? { label: pair.visibilityChange === 'hidden' ? 'Newly hidden' : 'Newly unhidden', tone: 'changed' }
      : catalogVisibilityBadge(pair[side]);
  return [visibilityBadge, pairChangeBadge(pair, side)].filter(Boolean);
};

const pairChangeBadge = (pair, side) => {
  if (pair.changeType === 'removed' && side === 'current') return { label: CATALOG_CHANGE_LABELS.removed, tone: 'removed' };
  if (!pair.changeType || side !== 'proposed') return null;
  // The visibility badge already shows a visibility change, so this badge names the next change.
  const headline = pair.changeType === 'visibility' ? (pair.changeTypes || []).find(type => type !== 'visibility') : pair.changeType;
  return headline ? { label: CATALOG_CHANGE_LABELS[headline], tone: CHANGE_TONES[headline] } : null;
};

// One line per change the pair carries, in change-type order, naming counts and the variants siblings moved between.
export const catalogChangeNotes = (pair, side) => {
  if (side === 'current') return pair.changeType === 'removed' ? [removedNote(pair)] : [];
  const counts = pair.changeCounts || {};
  const notes = [];
  if (pair.changeType === 'added') notes.push(addedNote(pair));
  if (counts.renamed) notes.push(`Renamed from “${pair.current.name}”`);
  if (counts.visibility) notes.push(pair.visibilityChange === 'hidden' ? 'Publishing hides this variant from the public catalog' : 'Publishing makes this variant publicly visible');
  if (pair.changeType !== 'added') {
    notes.push(...moveNotes(pair.movedIn, 'from'), ...moveNotes(pair.movedOut, 'to'));
    notes.push(countNote(counts.siblings_added, 'sibling added', 'siblings added'));
    notes.push(...removalNotes(pair));
    notes.push(...excludedNotes(pair.excluded));
  }
  notes.push(countNote(counts.non_size_tags_changed, 'non-size tag changed', 'non-size tags changed'));
  notes.push(countNote(counts.size_tags_changed, 'size tag changed', 'size tags changed'));
  notes.push(countNote(counts.skus_changed, 'SKU changed', 'SKUs changed'));
  notes.push(countNote(counts.urls_changed, 'URL changed', 'URLs changed'));
  notes.push(countNote(counts.ids_changed, 'Shopify ID changed', 'Shopify IDs changed'));
  return notes.filter(Boolean);
};

const cardNote = notes => {
  if (notes.length <= CARD_NOTE_LIMIT) return notes.join(' · ') || null;
  return [...notes.slice(0, CARD_NOTE_LIMIT), `+${notes.length - CARD_NOTE_LIMIT} more`].join(' · ');
};

const addedNote = pair => {
  const moves = pair.movedIn || [];
  if (!moves.length) return 'New variant from the latest snapshot';
  const names = moves.map(moveName).join(', ');
  const hasNewSiblings = Boolean(pair.changeCounts?.siblings_added);
  if (moves.length === 1 && !hasNewSiblings) return `Split from ${names}`;
  return `Formed from ${names}${hasNewSiblings ? ' and new siblings' : ''}`;
};

const removedNote = pair => {
  const moves = pair.movedOut || [];
  const counts = pair.changeCounts || {};
  const exits = (counts.siblings_removed || 0) + (counts.siblings_excluded || 0);
  if (moves.length === 1 && !exits) return `Merged into ${moveName(moves[0])}`;
  if (moves.length > 1 && !exits) return `Split into ${moves.map(moveName).join(', ')}`;
  const notes = [...moveNotes(moves, 'to'), ...removalNotes(pair), ...excludedNotes(pair.excluded)].filter(Boolean);
  return notes.join(' · ') || 'Sources no longer in the snapshot';
};

const moveNotes = (moves, direction) => (moves || []).map(move => `${countNote(move.count, 'sibling', 'siblings')} moved ${direction} ${moveName(move)}`);

const removalNotes = pair => {
  const scriptRemoved = pair.scriptRemovedSiblingCount || 0;
  return [
    countNote((pair.changeCounts?.siblings_removed || 0) - scriptRemoved, 'sibling left the snapshot', 'siblings left the snapshot'),
    countNote(scriptRemoved, 'sibling removed by script', 'siblings removed by script')
  ];
};

const moveName = move => (move.isHidden ? `${move.name} (hidden)` : move.name);

const excludedNotes = excluded => (excluded || []).map(row => `${row.reason} (${countNote(row.count, 'sibling', 'siblings')})`);

const countNote = (count, singular, plural) => (count ? `${count.toLocaleString()} ${count === 1 ? singular : plural}` : null);

export default CatalogCompareGrid;
